import {
  type FieldProvenanceDto,
  LlmStreamStatus,
  type MissionAssessmentCapabilityAiGenerationDataDto,
  type MissionAssessmentCapabilityAiGenerationResponseDto,
  type MissionAssessmentCapabilityAiQueryDto,
  MissionAssessmentChatObjectKind,
} from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { Avatar } from '@components/shared/avatar.component';
import { CapabilitySelectOption, CapabilitySelectedBadge } from '@components/shared/capability-definition.component';
import { FieldProvenanceBadge } from '@components/shared/field-provenance-badge.component';
import { useFormSuggestion } from '@components/shared/form-suggestion.component';
import { GenerateJSONButton } from '@components/shared/generate-json-button.component';
import { Button } from '@components/ui/button';
import { withForm } from '@components/ui/form/form';
import { config } from '@config';
import maDefinitions from '@data/ma-definitions.json';
import { useGetCapabilities } from '@queries/capability.query';
import { useGetCommunities } from '@queries/community.query';
import { useGetKillChains } from '@queries/kill-chain.query';
import { useGenerateMissionAssessmentCapabilities } from '@queries/mission-assessments.query';
import { useGetServices } from '@queries/service.query';
import {
  missionAssessmentBaseSchema,
  missionAssessmentServiceCreateSchema,
  missionAssessmentTaskRecommendationCreateSchema,
} from '@schemas/mission-assessment.schema';
import { countries } from '@utils/constants';
import { sectionProvenance } from '@utils/mission-assessment-provenance.util';
import { PlusIcon, SparklesIcon, XIcon } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { MISSION_ASSESSMENT_AI_ENDPOINTS } from '../../../../constants/mission-assessment-endpoints.constants';
import { TooltipComponent } from '../../../core/tooltip.component';
import {
  MissionAssessmentCapabilitySuggestion,
  MissionAssessmentCapabilitySuggestionBadges,
} from './mission-assessment-capability-suggestion.component';
import {
  parseGeneratedCapabilityData,
  replaceCapabilitiesWithSuggestion,
} from './mission-assessment-capability-suggestion.util';
import { missionAssessmentDraftFormOptions } from './mission-assessment-form-options';

const CAPABILITY_SUGGESTION_KEY = 'capabilities';
const isAIEnabled = config.ai.enabled;

const getCountryCode = (serviceName: string): string | undefined => {
  const country = countries.find((c) => c.name === serviceName);
  return country?.code;
};

export const MissionAssessmentFormServices = withForm({
  ...missionAssessmentDraftFormOptions,
  props: {
    provenance: undefined as Record<string, FieldProvenanceDto> | null | undefined,
    submitAction: undefined as 'draft' | 'complete' | null | undefined,
    technologyId: undefined as string | undefined,
  },
  render: function Render({ form, provenance, submitAction = null, technologyId }) {
    const isComplete = submitAction === 'complete';
    const { data: availableCapabilities, isLoading: isLoadingCapabilities } = useGetCapabilities();
    const { data: availableCommunities, isLoading: isLoadingCommunities } = useGetCommunities();
    const { data: availableKillChains, isLoading: isLoadingKillChains } = useGetKillChains();
    const { data: availableServices, isLoading: isLoadingServices } = useGetServices();
    const { mutateAsync: generateCapabilities } = useGenerateMissionAssessmentCapabilities();
    const {
      acceptSuggestion,
      rejectSuggestion,
      getIsGenerating,
      getSuggestion,
      getSuggestionModel,
      handleGeneratingEnd,
      handleGeneratingStart,
      handleJsonData,
      handleMetadata,
    } = useFormSuggestion<string, MissionAssessmentCapabilityAiGenerationDataDto>();

    const suggestion = getSuggestion(CAPABILITY_SUGGESTION_KEY);
    const capabilitySuggestion = typeof suggestion === 'object' ? suggestion : null;
    const isGeneratingCapabilities = getIsGenerating(CAPABILITY_SUGGESTION_KEY);

    const capabilityOptions = useMemo(() => {
      if (!availableCapabilities) return [];
      return availableCapabilities.map((capability) => ({
        description: capability.description,
        label: capability.name,
        value: capability.id,
      }));
    }, [availableCapabilities]);

    const communityOptions = useMemo(() => {
      if (!availableCommunities) return [];
      return availableCommunities.map((community) => ({
        label: community.name,
        value: community.id,
      }));
    }, [availableCommunities]);

    const killChainOptions = useMemo(() => {
      if (!availableKillChains) return [];
      return availableKillChains.map((killChain) => ({
        group: killChain.category,
        label: killChain.name,
        value: killChain.id,
      }));
    }, [availableKillChains]);

    const serviceOptions = useMemo(() => {
      if (!availableServices) return [];
      return availableServices.flatMap((service) =>
        (service.subServices || []).map((subService) => ({
          value: subService.id,
          label: `${getCountryCode(service.name) ? `${getCountryCode(service.name)} - ` : ''}${subService.name}`,
        })),
      );
    }, [availableServices]);

    const getSlugFromServiceId = (serviceId: string | undefined): string | undefined => {
      if (!serviceId) return undefined;
      const subServices = availableServices?.flatMap((service) => service.subServices || []) ?? [];
      const subService = subServices.find((subService) => subService.id === serviceId);
      return subService?.slug ?? undefined;
    };

    const getImageUrlFromServiceId = (serviceId?: string) => {
      if (!serviceId) return undefined;
      const slug = getSlugFromServiceId(serviceId);
      return slug ? `/images/services/${slug}.png` : undefined;
    };

    const handleCapabilityGenerationSuccess = useCallback(
      (result: MissionAssessmentCapabilityAiGenerationResponseDto) => {
        handleJsonData({ data: result.data, key: CAPABILITY_SUGGESTION_KEY });
        handleMetadata({
          key: CAPABILITY_SUGGESTION_KEY,
          metadata: {
            actualModel: result.metadata.actualModel,
            logId: result.metadata.logId,
            requestedModel: result.metadata.requestedModel,
            status: LlmStreamStatus.Completed,
          },
        });
      },
      [handleJsonData, handleMetadata],
    );

    const applyCapabilityGenerationData = useCallback(
      (data: MissionAssessmentCapabilityAiGenerationDataDto) => {
        form.setFieldValue(
          'capabilities',
          replaceCapabilitiesWithSuggestion({
            currentCapabilities: form.state.values.capabilities ?? [],
            suggestion: data,
          }),
        );
      },
      [form],
    );

    const handleAcceptCapabilitySuggestion = useCallback(() => {
      acceptSuggestion(CAPABILITY_SUGGESTION_KEY, (data: string | MissionAssessmentCapabilityAiGenerationDataDto) => {
        if (typeof data !== 'object') return;

        applyCapabilityGenerationData(data);
      });
    }, [acceptSuggestion, applyCapabilityGenerationData]);

    return (
      <section>
        <h4 className="flex items-center gap-2 text-lg mb-4 text-gray-700">
          Task Recommendations
          <FieldProvenanceBadge
            context="form"
            provenance={sectionProvenance(provenance, 'TASK_RECOMMENDATIONS')}
          />
        </h4>
        <div className="space-y-4">
          <form.AppField
            mode="array"
            name="taskRecommendations"
          >
            {(field) => (
              <div className="space-y-4">
                {field.state.value?.map((_, i) => (
                  <div
                    className="flex items-start gap-2 p-4 border border-gray-200 rounded-md"
                    key={i}
                  >
                    <div className="grow">
                      <form.AppField
                        name={`taskRecommendations[${i}].description`}
                        validators={{
                          onChange: missionAssessmentTaskRecommendationCreateSchema.shape.description,
                        }}
                      >
                        {(descriptionField) => (
                          <descriptionField.TextAreaField
                            name={`taskRecommendations[${i}].description`}
                            placeholder="Enter a task recommendation..."
                            required={isComplete}
                            rows={3}
                          />
                        )}
                      </form.AppField>
                    </div>
                    <IconButton
                      ariaLabel="Remove task recommendation"
                      icon={<XIcon size={16} />}
                      onClick={() => field.removeValue(i)}
                      size="lg"
                      variant="secondary"
                    />
                  </div>
                ))}
                <Button
                  className="h-auto mt-4"
                  onClick={() => field.pushValue({ description: '' })}
                  variant="link"
                >
                  <PlusIcon className="size-4" />
                  Add Task Recommendation
                </Button>
              </div>
            )}
          </form.AppField>
        </div>

        <div className="h-px my-8 bg-gray-200 w-full" />

        <div className="flex items-center gap-2 mb-4">
          <h4 className="flex items-center gap-2 text-lg text-gray-700">
            Recommended Services
            <FieldProvenanceBadge
              context="form"
              provenance={sectionProvenance(provenance, 'RECOMMENDED_SERVICES')}
            />
          </h4>
          <TooltipComponent
            className="text-xs"
            side="right"
            triggerClassName="text-gray-500 bg-white"
          >
            {maDefinitions?.service}
            {'\n\n'}
            {maDefinitions?.unit}
          </TooltipComponent>
        </div>
        <div className="space-y-4">
          <form.AppField
            mode="array"
            name="recommendedServices"
          >
            {(field) => (
              <div className="space-y-4">
                {field.state.value?.map((_, i) => (
                  <div
                    className="flex items-center gap-2 p-2 border border-gray-200 rounded-md"
                    key={i}
                  >
                    <form.Subscribe selector={(state) => state.values.recommendedServices}>
                      {(recommendedServices) => (
                        <Avatar
                          altText={
                            getSlugFromServiceId(recommendedServices?.[i]?.serviceId) ??
                            serviceOptions.find((option) => option.value === recommendedServices?.[i]?.serviceId)
                              ?.label ??
                            'Service Logo'
                          }
                          imageUrl={getImageUrlFromServiceId(recommendedServices?.[i]?.serviceId)}
                          initialText={getSlugFromServiceId(recommendedServices?.[i]?.serviceId)}
                          size="lg"
                        />
                      )}
                    </form.Subscribe>

                    <div className="flex flex-col gap-2 w-full">
                      <div className="flex items-start space-x-2">
                        <div className="grow flex flex-col gap-1">
                          <form.AppField
                            name={`recommendedServices[${i}].serviceId`}
                            validators={
                              isComplete
                                ? {
                                    onChange: missionAssessmentServiceCreateSchema.shape.serviceId,
                                  }
                                : {}
                            }
                          >
                            {(serviceField) => (
                              <serviceField.SelectPopoverField
                                disabled={isLoadingServices}
                                emptyMessage="No services found."
                                formatValue={(option) => (option ? `${option.label}` : 'Select service')}
                                options={serviceOptions}
                                placeholder="Select service"
                                searchPlaceholder="Search services..."
                                tooltip={maDefinitions?.service}
                              />
                            )}
                          </form.AppField>
                        </div>
                        <IconButton
                          ariaLabel="Remove service"
                          icon={<XIcon size={16} />}
                          onClick={() => field.removeValue(i)}
                          size="lg"
                          variant="secondary"
                        />
                      </div>
                      <form.AppField
                        name={`recommendedServices[${i}].unit`}
                        validators={
                          isComplete
                            ? {
                                onBlur: missionAssessmentServiceCreateSchema.shape.unit,
                              }
                            : {}
                        }
                      >
                        {(unitField) => (
                          <unitField.TextField
                            name={`recommendedServices[${i}].unit`}
                            placeholder="e.g., Space and Missile Research Unit"
                            required={isComplete}
                            tooltip={maDefinitions?.unit}
                          />
                        )}
                      </form.AppField>
                    </div>
                  </div>
                ))}
                <Button
                  className="h-auto mt-4"
                  onClick={() => field.pushValue({ serviceId: '', unit: '' })}
                  variant="link"
                >
                  <PlusIcon className="size-4" />
                  Add Service
                </Button>
              </div>
            )}
          </form.AppField>
        </div>

        <div className="h-px my-8 bg-gray-200 w-full" />

        <div className="space-y-4">
          <form.AppField
            name="communities"
            validators={{
              onBlur: missionAssessmentBaseSchema.shape.communities,
            }}
          >
            {(field) => (
              <field.SelectMultipleField
                label="Functional Communities"
                labelSuffix={
                  <FieldProvenanceBadge
                    context="form"
                    provenance={sectionProvenance(provenance, 'COMMUNITIES')}
                  />
                }
                disabled={isLoadingCommunities}
                options={communityOptions}
                placeholder="Select communities"
                required
                tooltip={maDefinitions?.functionalCommunities}
              />
            )}
          </form.AppField>

          <form.AppField
            name="capabilities"
            validators={{
              onBlur: missionAssessmentBaseSchema.shape.capabilities,
            }}
          >
            {(field) => (
              <>
                <field.SelectMultipleField
                  label="Joint Capabilities"
                  labelSuffix={
                    <FieldProvenanceBadge
                      context="form"
                      provenance={sectionProvenance(provenance, 'CAPABILITIES')}
                    />
                  }
                  disabled={isLoadingCapabilities}
                  options={capabilityOptions}
                  placeholder="Select capabilities"
                  renderBadge={(params) => <CapabilitySelectedBadge {...params} />}
                  renderOption={(option) => <CapabilitySelectOption option={option} />}
                  required
                  tooltip={maDefinitions?.jointCapabilities}
                  trailingAction={
                    technologyId && isAIEnabled ? (
                      <GenerateJSONButton<
                        MissionAssessmentCapabilityAiQueryDto,
                        MissionAssessmentCapabilityAiGenerationResponseDto
                      >
                        abortable
                        buttonProps={{
                          'aria-label': 'Generate Joint Capabilities',
                          children: <SparklesIcon className="size-4" />,
                          disabled: isLoadingCapabilities || !availableCapabilities?.length,
                          size: 'icon',
                          variant: 'ghost',
                        }}
                        className="size-7 min-w-7 text-gray-400 hover:bg-[#e0e0f7] hover:text-[#3D2EFF]"
                        description={
                          <>
                            Use Tyr&apos;s AI assistant to select Joint Capabilities for this Mission Assessment. All
                            relevant metadata and Joint Capability definitions will be used.
                          </>
                        }
                        generatingChildren={<SparklesIcon className="size-4 animate-pulse" />}
                        chatApiEndpoint={MISSION_ASSESSMENT_AI_ENDPOINTS.chat}
                        chatData={{
                          currentCapabilities: field.state.value ?? [],
                          objectKind: MissionAssessmentChatObjectKind.CAPABILITIES,
                          technologyId,
                        }}
                        isGenerating={isGeneratingCapabilities}
                        mutateAsync={generateCapabilities}
                        onAcceptData={(value) => {
                          const parsed = parseGeneratedCapabilityData(value);
                          if (parsed) applyCapabilityGenerationData(parsed);
                        }}
                        onGeneratingEnd={() => handleGeneratingEnd(CAPABILITY_SUGGESTION_KEY)}
                        onGeneratingStart={() => handleGeneratingStart(CAPABILITY_SUGGESTION_KEY)}
                        onSuccess={handleCapabilityGenerationSuccess}
                        params={{
                          currentCapabilities: field.state.value ?? [],
                          technologyId,
                        }}
                        renderDataArtifact={(value) => {
                          const parsed = parseGeneratedCapabilityData(value);
                          if (!parsed) return null;

                          return (
                            <MissionAssessmentCapabilitySuggestionBadges
                              capabilities={availableCapabilities ?? []}
                              data={parsed}
                            />
                          );
                        }}
                        title="Joint Capabilities"
                      />
                    ) : undefined
                  }
                />

                {technologyId && isAIEnabled && !isGeneratingCapabilities && (
                  <MissionAssessmentCapabilitySuggestion
                    capabilities={availableCapabilities ?? []}
                    data={capabilitySuggestion}
                    isGenerating={false}
                    model={getSuggestionModel(CAPABILITY_SUGGESTION_KEY)}
                    onAccept={handleAcceptCapabilitySuggestion}
                    onReject={() => rejectSuggestion(CAPABILITY_SUGGESTION_KEY)}
                  />
                )}
              </>
            )}
          </form.AppField>

          <form.AppField
            name="killChains"
            validators={{
              onBlur: missionAssessmentBaseSchema.shape.killChains,
            }}
          >
            {(field) => (
              <field.SelectMultipleField
                disabled={isLoadingKillChains}
                label="Kill Chain Element"
                labelSuffix={
                  <FieldProvenanceBadge
                    context="form"
                    provenance={sectionProvenance(provenance, 'KILL_CHAINS')}
                  />
                }
                options={killChainOptions}
                placeholder="Select kill chains"
              />
            )}
          </form.AppField>
        </div>
      </section>
    );
  },
});
